import { useState, useEffect } from "react";
import {
  OutreachCategory,
  genreOptions,
  ReactionChannel,
  PlaylistingChannel,
  SpotifyPlaylist,
  MusicBlog,
  MusicIndustry,
  Campaign,
} from "../types/outreach";

// Type for the form state
export type OutreachItem =
  | ReactionChannel
  | PlaylistingChannel
  | SpotifyPlaylist
  | MusicBlog
  | MusicIndustry
  | Campaign;
export type OutreachFormItem = Partial<OutreachItem> & Record<string, any>;

interface UseOutreachFormReturn {
  dialogOpen: boolean;
  dialogMode: "add" | "edit";
  currentItem: OutreachFormItem | null;
  openAddDialog: () => void;
  openEditDialog: (item: OutreachFormItem) => void;
  closeDialog: () => void;
  handleFieldChange: (field: string, value: any) => void;
  getEmptyItem: (category: OutreachCategory) => OutreachFormItem;
}

interface UseOutreachFormProps {
  currentCategory: OutreachCategory;
}

export const useOutreachForm = (
  { currentCategory }: UseOutreachFormProps = {
    currentCategory: OutreachCategory.REACTION_CHANNELS,
  }
): UseOutreachFormReturn => {
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [dialogMode, setDialogMode] = useState<"add" | "edit">("add");
  const [currentItem, setCurrentItem] = useState<OutreachFormItem | null>(null);

  // Get empty item template based on category
  const getEmptyItem = (category: OutreachCategory): OutreachFormItem => {
    switch (category) {
      case OutreachCategory.REACTION_CHANNELS:
        return {
          channelName: "",
          channelUrl: "",
          subscriberCount: "",
          genre: "",
          email: "",
          socialHandles: "",
          socialLinks: [],
        };
      case OutreachCategory.PLAYLISTING_CHANNELS:
        return {
          channelName: "",
          channelUrl: "",
          subscriberCount: "",
          genre: "",
          email: "",
        };
      case OutreachCategory.SPOTIFY_PLAYLISTS:
        return {
          playlistName: "",
          playlistUrl: "",
          curatorSocialHandle: "",
          source: "",
          genre: "",
          curatorEmail: "",
          playlistLikes: 0,
          playlistFollowers: 0,
        };
      case OutreachCategory.MUSIC_BLOGS:
        return {
          blogName: "",
          contact: "",
          blogUrl: "",
          genre: "",
          monthlyVisits: 0,
        };
      case OutreachCategory.CAMPAIGNS:
        return {
          name: "",
          description: "",
          genre: "",
          tag: null,
          tagId: null,
          recipients: [],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          saved: false,
        };
      default:
        return {};
    }
  };

  useEffect(() => {
    // If dialog is open and we're in add mode, update the form with the new category's empty item
    if (dialogOpen && dialogMode === "add") {
      setCurrentItem(getEmptyItem(currentCategory));
    }
  }, [currentCategory, dialogOpen, dialogMode]);

  // Open dialog for adding a new item
  const openAddDialog = () => {
    setDialogMode("add");
    setCurrentItem(getEmptyItem(getCurrentCategory()));
    setDialogOpen(true);
  };

  // Open dialog for editing an existing item
  const openEditDialog = (item: OutreachFormItem) => {
    setDialogMode("edit");
    setCurrentItem({ ...item });
    setDialogOpen(true);
  };

  // Handle dialog close
  const closeDialog = () => {
    setDialogOpen(false);
    setCurrentItem(null);
  };

  // Handle form field changes
  const handleFieldChange = (field: string, value: any) => {
    setCurrentItem((prev) => {
      if (!prev) return null;
      return {
        ...prev,
        [field]: value,
      };
    });
  };

  // Helper function to get current category
  const getCurrentCategory = (): OutreachCategory => {
    return currentCategory;
  };

  return {
    dialogOpen,
    dialogMode,
    currentItem,
    openAddDialog,
    openEditDialog,
    closeDialog,
    handleFieldChange,
    getEmptyItem,
  };
};
